'use client';

// Requirements Summary:
// Public catalog cards use coach wording and respect the program setting that
// controls whether family-facing coach names include gender.
//
// OPEN GYM (U5, R7): a class flagged is_open_gym gets its own catalog identity —
// a distinct "Open Gym" badge (replacing the generic "Classes" label), a
// distinct cover icon (warehouse), and a "Book a session" CTA on its detail
// page instead of "Enroll Now". The card itself says "View Details". The booking link
// still points at the canonical class detail/enroll path (no forked route); the
// drop-in date picker on that path handles the actual booking. The member-rate
// badge ("$X / $Y members") still renders alongside this, at the lower of the
// two rates, and only when that discount is actually offered.
//
// FAMILY / GUARDIAN ACCESS (FGA) — catalog operates in the ACTIVE family's mode:
// When the ENABLE_FAMILY_GUARDIAN_ACCESS flag is ON and the viewer is acting as a
// family they CANNOT register for (no "manage enrollments" permission), the
// Register / Enroll Now CTA on each program card is shown DISABLED with
// CANNOT_REGISTER_ATHLETE_MESSAGE instead of a working link — so the parent
// never starts a flow that ends in a permission error. Browsing programs stays
// open to everyone.
// SAFETY: this gate is fully inert (today's exact behavior) when the flag is off,
// for guests/logged-out visitors, for primary guardians, and whenever the active
// family belongs to a DIFFERENT gym than the one being browsed. The server-side
// enrollment permission check stays in place as the real backstop; this only adds
// an earlier, friendlier client-side signal. Copy mirrors the per-athlete picker
// reason used on the downstream enroll / private-lesson pages.

import { isDropInOffered } from '@/utils/programs/dropInOffered';
import { memberDropInPriceToShow } from '@/utils/pricing/memberRateHigherThanStandard';
import { classSellsMonthly } from '@/utils/programs/classSellsMonthly';
import { useState, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import GoogleMaterialIcon from '@/components/ui/GoogleMaterialIcon';
import { createLogger } from '@/utils/logger/devLogger';
import { useUserRole } from '@/context/UserRoleContext';
import { useSelfServiceAccess } from '@/hooks/auth/useSelfServiceAccess';
import { useOrganization } from '@/context/OrganizationContext';
import { useFamilyAthleteGate } from '@/hooks/family/useFamilyAthleteGate';
import { CANNOT_REGISTER_ATHLETE_MESSAGE } from '@/utils/family/registrationPermissionCopy';
import { SwitchFamilyButton, useCanSwitchFamily } from '@/components/family/SwitchFamilyButton';
import { Dropdown } from '@/components/ui/dropdown/Dropdown';
import { tryoutDetailPath } from '@/utils/url/programCatalogPath';
import { PublicAccountMenu, PublicAccountMenuItems, isSameAccount } from '@/components/common/PublicAccountMenu';
import { useIsMobileWebView } from '@/hooks/useIsMobileWebView';
import { openProgramTarget } from '@/utils/navigation/openProgramTarget';
import { Button } from '@/components/ui/button';
import { TextField, SearchField, SelectField } from '@/components/form/fields';
import { formatTimeTo12Hour, formatDateRange, formatDate, parseDateLocal } from '@/utils/formatters/dateUtils';
import { summarizeWeeklyDays, type WeeklyDayGroup } from '@/utils/classes/weeklyDaySummary';
import { getGymNowParts } from '@/utils/programs/nextOpenGymSession';
import { CLASS_CATEGORIES } from '@/utils/types/classTypes';
import { installmentCountFromPlan, SESSION_PAYMENT_PLAN_OPTIONS, type SessionPaymentPlan } from '@/utils/billing/sessionPaymentPlan';
import DayBadges, { dayCodeFromIndex, type DayCode } from '@/components/ui/DayBadges';
import { supabase } from '@/utils/supabase/client';
import { Modal } from '@/components/ui/modal';
import Alert from '@/components/ui/alert/Alert';
import CoachAvatar from '@/components/ui/avatar/CoachAvatar';
import { DEFAULT_PRIMARY_COLOR } from '@/utils/constants/brandingDefaults';
import { brandTextColor, readableTextOn, SURFACE_GRAY_50 } from '@/utils/branding/brandColorContrast';
import {
  GYM_LOOKUP_UNAVAILABLE_SCREEN,
  GYM_NOT_FOUND_SCREEN,
} from '@/utils/constants/publicGymLookupCopy';
import { isTryoutRegistrationOpen } from '@/utils/tryouts/registrationStatus';
import { DEFAULT_SERVER_TIMEZONE } from '@/utils/timezone/serverLocalDate';
import { isProgramEnded } from '@/utils/programs/isProgramEnded';
import { getPublicEnrollmentGating } from '@/utils/programs/openGymEnrollmentGating';
import {
  arrangeCatalogSections,
  groupIdOf,
  groupsInUse,
  MORE_PROGRAMS_TITLE,
  MORE_TITLE_BY_TAB,
  type CatalogClassGroup,
} from '@/utils/programs/catalogClassGroups';
import { GenderUtils } from '@/utils/constants/genderConstants';
import SummaryBelowPlaceholder from '@/components/seo/SummaryBelowPlaceholder';
import { useKeepScrollAcrossReload } from '@/hooks/ui/useKeepScrollAcrossReload';
import { isInvitedAthleteLogin } from '@/utils/auth/parentDashboardAccess';
import { FEATURE_FLAGS } from '@/utils/config/featureFlags';
import PublicEventsList, { EVENTS_CATALOG_TAB, usePublicTicketedEvents } from '@/components/events/tickets/PublicEventsList';

const logger = createLogger('PublicProgramCatalog');


interface ClassScheduleSlot {
  day_of_week: number; // 0 = Sunday, 1 = Monday, etc.
  start_time: string;
  end_time: string;
  location_id?: string;
  instructor_id?: string;
}

interface EventDay {
  event_date: string;
  start_time: string;
  end_time: string;
  day_label?: string;
  day_number: number;
}

interface TryoutEvent {
  id: string;
  name: string;
  event_date: string;
  start_time: string;
  end_time: string;
  registration_opens_at: string | null;
  registration_closes_at: string | null;
  status: 'upcoming' | 'open' | 'closed' | 'completed' | 'cancelled';
  max_participants: number | null;
  current_registrations: number;
}

interface Program {
  id: string;
  name: string;
  program_type: 'class' | 'team' | 'private_lessons';
  description: string | null;
  cover_image_url: string | null;
  age_range_min: number | null;
  age_range_max: number | null;
  skill_level: string | null;
  schedule_description: string | null;
  registration_opens_at?: string | null; // For tryout events
  registration_closes_at?: string | null; // For tryout events
  class_category?: string | null; // For class programs
  catalog_group_id?: string | null; // #6005: the gym's group for this class
  catalog_sort_order?: number | null; // #6005: place inside that group
  team_type?: string | null; // For team programs
  season_name?: string | null; // For team programs
  season_start_date?: string | null; // For team programs
  season_end_date?: string | null; // For team programs
  location?: {  // Single location (camps/events, teams)
    id: string;
    name: string;
    city: string;
    state: string;
  } | null;
  schedule_locations?: Array<{  // Multiple locations (classes)
    id: string;
    name: string;
    city: string;
    state: string;
  }>;

  // Set by the browse route when this offering's payment cannot be routed: every
  // location it is available at is blocked, or — for an offering with no location
  // at all, such as a tryout with no venue set — the gym's default destination is.
  // Absent means "not blocked"; a gym with nothing blocked never carries the key.
  registration_locked?: boolean;
  
  // Scheduling details
  sessions_per_week: number | null;
  session_duration: number | null;
  daily_start_time: string | null;
  daily_end_time: string | null;
  specific_dates: string[] | null;
  event_duration_days: number | null;
  class_schedule: ClassScheduleSlot[] | null;
  weekly_day_groups?: WeeklyDayGroup[];
  sections?: Array<{
    id: string;
    section_name: string;
    capacity: number;
    current_enrollment?: number;
    spots_remaining?: number | null;
    waitlist_enabled?: boolean;
    monthly_rate: string | number;
    drop_in_rate: string | number;
    location?: {
      id: string;
      name: string;
      city: string;
      state: string;
    };
    instructor?: {
      id: string;
      preferred_name?: string | null;
      auth_user?: {
        first_name: string;
        last_name: string;
        avatar_url: string | null;
      };
    };
    // New grouped format: array of schedules
    schedules?: Array<{
      id: string;
      day_of_week: number;
      start_time: string;
      end_time: string;
      location?: {
        id: string;
        name: string;
        city: string;
        state: string;
      };
      instructor?: {
        id: string;
        preferred_name?: string | null;
        auth_user?: {
          first_name: string;
          last_name: string;
          avatar_url: string | null;
        };
      };
      instructors?: Array<{
        id: string;
        preferred_name?: string | null;
        auth_user?: {
          first_name: string;
          last_name: string;
          avatar_url: string | null;
        };
      }>;
      instructor_name?: string | null;
      instructor_names?: string[];
    }>;
    // Legacy format: single schedule (for backward compatibility)
    day_of_week?: number;
    start_time?: string;
    end_time?: string;
  }>;
  event_days?: EventDay[] | null; // Multi-day tryout support
  
  // Pricing
  monthly_rate: number | null;
  monthly_membership_enabled?: boolean;
  drop_in_rate: number | null;
  drop_in_enabled?: boolean;
  // Open Gym U5 (R7): discriminator the catalog keys on for the distinct
  // identity — an "Open Gym" badge, a distinct cover icon, and a "Book a
  // session" CTA (instead of "Enroll Now"). Server-supplied (browse route maps
  // is_open_gym for class programs, false for everything else), so the catalog
  // never has to infer it from other class flags.
  is_open_gym?: boolean;
  // Open Gym catalog "Next session" line (PR1/U1): the soonest upcoming
  // session's gym-local day label + raw times, computed server-side by the
  // browse route (day + time only, no live spots). null / absent when the
  // program is not an open gym, is staff-gated, or has no upcoming session.
  next_session?: { dayLabel: string; startTime: string; endTime: string } | null;
  // Open Gym U6 (R12): member drop-in rate, shown publicly as "$X / $Y members"
  // when enabled (badge can't read membership, so it shows both rates).
  member_drop_in_enabled?: boolean;
  member_drop_in_rate?: number | null;
  trial_rate: number | null;
  // GH #3667: tryout price for athletes already at the gym, or null when unset.
  member_trial_rate?: number | null;
  full_event_price: number | null; // For camps/events

  // Fixed-term (session-based) pricing
  is_session_based?: boolean;
  session_total_price?: number | null;
  session_payment_plan?: SessionPaymentPlan | null;
  
  // Enrollment
  capacity: number | null;
  current_enrollment: number;
  // null for open gyms: the browse API neutralizes the lifetime number there
  // because capacity is per session.
  spots_remaining: number | null;
  instructor_name: string | null;
  start_date: string | null;
  end_date: string | null;
  /** #4724: 'YYYY-MM-DD' while an ongoing class's first class is still ahead (gym-local); else null. */
  starts_on?: string | null;
  enrollment_status: 'open' | 'closed' | 'waitlist_only';
  waitlist_enabled?: boolean;
  visibility: 'visible' | 'hidden' | 'private_link';
  registration_type?: 'direct_enrollment' | 'evaluation_required';
  
  // Evaluation events (if applicable)
  tryout_events?: TryoutEvent[];
}

interface OrganizationInfo {
  // Carried so the catalog can verify the family-context's active org matches the
  // gym being browsed before applying any Family/Guardian Access gating (FGA).
  id?: string;
  name: string;
  logo_url: string | null;
  slug: string;
  branding?: {
    primary_color: string | null;
    secondary_color: string | null;
  };

  // GH #883 U5: true when the gym's Free plan cap is reached (grace consumed)
  // — new-athlete registration closes, browsing and existing-athlete
  // enrollment stay open. Server-resolved per request, never cached.
  roster_cap_blocked?: boolean;
  // True when the gym's merch store is open to families (platform flag on AND
  // the gym turned its store on). Shows the "Merch Store" chip in the category
  // row. Absent/false hides the chip — never a dead door.
  merch_store_open?: boolean;
}

type ProgramTypeFilter = 'all' | 'classes' | 'open_gym' | 'tryouts' | 'private_lessons' | 'events';
type AvailabilityFilter = 'all' | 'open' | 'limited' | 'waitlist' | 'tryouts' | 'drop_in';

// Helper: Extract days of week from class_schedule
/** Staff closed it, or (#5302) it is a class that sells nothing. Same rule as the class page. */
const isEnrollmentClosed = (program: Program): boolean =>
  getPublicEnrollmentGating({
    isOpenGym: program.program_type === 'class' && program.is_open_gym === true,
    enrollmentStatus: program.enrollment_status,
    spotsRemaining: program.spots_remaining,
    classOffer: program.program_type === 'class' ? program : null,
  }).closed;

const getDaysFromSchedule = (schedule: ClassScheduleSlot[] | null): DayCode[] => {
  if (!schedule || schedule.length === 0) return [];
  
  const dayMap: Record<number, DayCode> = {
    0: 'SU',
    1: 'MO',
    2: 'TU',
    3: 'WE',
    4: 'TH',
    5: 'FR',
    6: 'SA'
  };
  
  // Extract unique days and sort them
  const uniqueDays = [...new Set(schedule.map(slot => slot.day_of_week))]
    .sort()
    .map(dayNum => dayMap[dayNum])
    .filter(Boolean) as DayCode[];
  
  return uniqueDays;
};

/**
 * Which tab a program belongs to. Open Gym shares program_type='class' but gets
 * its own chip, so the Classes tab leaves it out and each program shows in
 * exactly one place.
 */
function matchesProgramTab(p: Program, tab: string): boolean {
  if (tab === 'all') return true;
  if (tab === 'classes') return p.program_type === 'class' && !(p as any).is_open_gym;
  if (tab === 'open_gym') return p.program_type === 'class' && (p as any).is_open_gym === true;
  if (tab === 'tryouts') return Boolean((p as any).is_tryout_event);
  return p.program_type === tab;
}

export interface ProgramCatalogClientProps {
  /**
   * The crawler-visible catalog summary the SERVER already rendered, or null
   * when this gym is not publishable.
   *
   * It is displayed below the loading skeleton, so the served HTML carries the
   * gym's real content without a person seeing it flash up, and the client
   * swaps both for the interactive catalog once its own fetch resolves. Passed
   * as a node rather than as data on purpose: the summary is a server
   * component, and rendering it here would make this whole route a client
   * render again.
   *
   * THE CLIENT KEEPS ITS OWN MOUNT FETCHES and is not seeded from this. It
   * consumes `roster_cap_blocked` and member-only rates, which the SEO fetcher
   * is forbidden to return (KTD5), and the catalog API is `force-dynamic`
   * precisely because the roster cap has to reopen on the next load — data an
   * hour-cached page can never own.
   */
  serverSummary?: ReactNode;
}

export default function ProgramCatalogClient({ serverSummary = null }: ProgramCatalogClientProps = {}) {
  const params = useParams();
  const router = useRouter();
  // Navigation adapters for openProgramTarget. Hoisted out of the CTA so the
  // call site names no window API of its own — catalogCtaCallSite.test.ts reads
  // that footer and rejects any direct new-tab open it finds there.
  const pushHref = (href: string) => router.push(href);
  const openHrefInNewTab = (href: string, target: string) => {
    window.open(href, target);
  };
  const gymSlug = params.gymSlug as string;
  const { currentUser, isLoading: loadingRole } = useUserRole();
  const selfServiceAccess = useSelfServiceAccess();
  const { currentOrganization } = useOrganization();
  // Plan 2026-05-23-002 PR #544 remediation (ADV-003): this check is the
  // "non-self-managing minor athlete" guard — it blocks accounts that
  // cannot enroll themselves. After U3's backfill of adult athletes, those
  // users resolve as userType='parent' (with a self-relationship row), so
  // this branch correctly does NOT fire for them — they fall through to
  // the parent enrollment path with their athlete-self pre-listed via the
  // relationship. Do NOT widen this to include backfilled adults; that
  // would block self-enrollment for the U3 cohort.
  const isInvitedAthlete = isInvitedAthleteLogin(currentUser);
  // Browsing is open to everyone; the card only labels why this viewer cannot
  // sign up. Covers confirmed staff AND a viewer we could not identify; the
  // program's own page shows the full StaffRegistrationMessage.
  const selfServiceUnavailable = selfServiceAccess === 'staff' || selfServiceAccess === 'unconfirmed';

  const [organization, setOrganization] = useState<OrganizationInfo | null>(null);

  // GH #5084: the gym's clock, and it must come from the BROWSE response.
  // `organization` above is loaded from /api/public/organizations/[gymSlug],
  // whose `timezone` is settings.timezone — a different column from the
  // gymlocations row getOrgTimezone reads, and therefore a second source that
  // could disagree with the register page. Kept in its own state, set from the
  // same fetch that sets `programs`, so the cards and the timezone that judges
  // them always arrive together. Fallback matches the server's own
  // (getOrgTimezone -> DEFAULT_SERVER_TIMEZONE).
  const [gymTimeZone, setGymTimeZone] = useState<string>(DEFAULT_SERVER_TIMEZONE);
  // A logo URL that 404s would otherwise leave an empty band with the gym's
  // name nowhere on screen, since the heading is only there for screen readers.
  const [logoFailed, setLogoFailed] = useState(false);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [filteredPrograms, setFilteredPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);
  // #2588: the full-screen failure carries its own heading, because "Error"
  // above try-again copy reads as permanent to a family.
  const [error, setError] = useState<{ heading: string; message: string } | null>(null);
  // Bumped by the "Try again" button to re-run the fetches below without a
  // full page reload. This is NOT auto-retry — the visitor asks for it.
  const [retryCount, setRetryCount] = useState(0);
  const [user, setUser] = useState<{ email: string; id: string } | null>(null);
  const [selectedProgramForSchedule, setSelectedProgramForSchedule] = useState<Program | null>(null);

  // Registration lock. `registrationLockNotice` is the browse route's own answer to
  // "does this gym have a lock at all" — it is NOT derived from the visible cards,
  // because a locked gym with zero programs still owes the parent the notice.
  // `lockedLocationNames` are the locations the notice may name.
  const [registrationLockNotice, setRegistrationLockNotice] = useState(false);
  const [lockedLocationNames, setLockedLocationNames] = useState<string[]>([]);

  // FAMILY / GUARDIAN ACCESS — the catalog reflects the family the parent is
  // currently acting as. The shared gate hook folds in the flag, the resolved
  // active family, and the org-match check (it stays inert otherwise), so the
  // catalog reads the same single source of truth as the downstream pickers
  // instead of re-deriving those preconditions here.
  const familyGate = useFamilyAthleteGate(organization?.id ?? null);

  // Whether to offer the family switcher in this catalog's header. The switcher
  // itself already no-ops for flag-off / single-family / signed-out users, so this
  // is just the extra "same gym" guard: the family context keys off the user's
  // dashboard org, so we only surface it when that matches the gym being browsed
  // (mirrors the gate's org-match). On another gym's catalog the gate is inert, so
  // showing this gym's-unrelated households would be misleading.
  const familyContextMatchesThisGym =
    !!currentOrganization?.id &&
    !!organization?.id &&
    currentOrganization.id === organization.id;

  const getInstructorDisplayName = (instructor: {
    preferred_name?: string | null;
    auth_user?: {
      first_name?: string | null;
      last_name?: string | null;
      gender?: string | null;
    };
  } | null | undefined) => {
    if (!instructor) return null;
    const genderLabel = instructor.auth_user?.gender && instructor.auth_user.gender !== 'prefer_not_to_say'
      ? ` (${GenderUtils.getGenderLabel(instructor.auth_user.gender)})`
      : '';
    if (instructor.auth_user?.first_name?.trim()) {
      const firstName = instructor.auth_user.first_name.trim();
      const lastInitial = instructor.auth_user.last_name?.trim()?.charAt(0);
      return `${lastInitial ? `${firstName} ${lastInitial}.` : firstName}${genderLabel}`;
    }
    if (instructor.preferred_name?.trim()) {
      const displayName = formatCoachDisplayName(instructor.preferred_name);
      return displayName ? `${displayName}${genderLabel}` : null;
    }

    return null;
  };

  const formatCoachDisplayName = (name: string | null | undefined) => {
    const parts = typeof name === 'string'
      ? name.trim().split(/\s+/).filter(Boolean)
      : [];

    if (parts.length <= 1) return parts[0] || null;

    return `${parts[0]} ${parts[parts.length - 1].charAt(0)}.`;
  };

  // Requirement summary: Public schedule cards should surface remaining capacity per schedule so parents can pick the right option.
  const renderScheduleSpotCount = (section: { spots_remaining?: number | null; waitlist_enabled?: boolean }) => {
    // Open Gym: capacity is per session, so the lifetime spots pill is
    // meaningless — show plain-English per-session framing instead. Mirrors
    // the detail page's renderScheduleSpotCount.
    if (selectedProgramForSchedule?.program_type === 'class' && selectedProgramForSchedule?.is_open_gym) {
      return (
        <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset bg-green-50 text-green-700 ring-green-200">
          Spots open up each session
        </span>
      );
    }
    if (typeof section.spots_remaining !== 'number') return null;

    const spots = section.spots_remaining;
    const waitlistOpen = section.waitlist_enabled || selectedProgramForSchedule?.waitlist_enabled;
    const label = spots === 0
      ? waitlistOpen ? 'Waitlist Open' : 'Full'
      : `${spots} ${spots === 1 ? 'spot' : 'spots'} available`;
    const toneClass = spots === 0
      ? waitlistOpen
        ? 'bg-blue-50 text-blue-700 ring-blue-200'
        : 'bg-red-50 text-red-700 ring-red-200'
      : spots <= 3
        ? 'bg-amber-50 text-amber-700 ring-amber-200'
        : 'bg-green-50 text-green-700 ring-green-200';

    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${toneClass}`}>
        {label}
      </span>
    );
  };

  // Requirement summary: Public catalog schedule cards show the coaches assigned to each time slot, not just the section fallback.
  const renderScheduleCoaches = (schedule: any) => {
    const instructors = Array.isArray(schedule?.instructors) ? schedule.instructors.filter(Boolean) : [];
    const displayableInstructors = instructors
      .map((instructor: any) => ({
        instructor,
        name: getInstructorDisplayName(instructor),
      }))
      .filter(({ name }) => Boolean(name));
    const names = displayableInstructors.length > 0
      ? displayableInstructors.map(({ name }) => name).filter(Boolean)
      : (schedule?.instructor_names || [schedule?.instructor_name])
        .map(formatCoachDisplayName)
        .filter(Boolean);

    if (names.length === 0) return null;

    return (
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 text-sm text-gray-500">
        <GoogleMaterialIcon name="person" size="sm" className="text-gray-400" />
        <span className="font-medium text-gray-600">
          Coach{names.length > 1 ? 'es' : ''}:
        </span>
        {displayableInstructors.length > 0 ? (
          <span className="flex flex-wrap items-center gap-2">
            {displayableInstructors.map(({ instructor, name }, instructorIndex: number) => {
              return (
                <span key={instructor.id || `${name}-${instructorIndex}`} className="inline-flex items-center gap-1.5">
                  <CoachAvatar
                    src={instructor.auth_user?.avatar_url || undefined}
                    name={name}
                    alt=""
                    size={24}
                    className="shrink-0"
                  />
                  <span>{name}{instructorIndex < displayableInstructors.length - 1 ? ',' : ''}</span>
                </span>
              );
            })}
          </span>
        ) : (
          <span>{names.join(', ')}</span>
        )}
      </div>
    );
  };

  // NO usePageTitle HERE, DELIBERATELY. The server page owns this route's title
  // through `generateMetadata`. Setting it again on mount would overwrite that
  // title in the browser — and Googlebot DOES render, so the title it finally
  // indexed would be "Program Catalog" rather than the gym's name and city.

  // Inside the app's webview the parent already has the app's own navigation, so
  // the web header's account controls are dead weight at best: Dashboard opened
  // the parent WEB dashboard on top of the sheet with no way back, and web
  // Sign In / Create Account are not the app's account flows.
  const isMobileWebView = useIsMobileWebView();
  // `user` holds only `{ id, email }` from the auth session. The role profile
  // adds a first name, but only when it is provably the SAME account — a stale
  // profile naming someone else would defeat the point of naming the account.
  const accountMenuUser =
    user && isSameAccount(currentUser?.email, user.email)
      ? { firstName: currentUser.firstName, lastName: currentUser.lastName, email: user.email }
      : user;
  // Does the family switcher actually have something to show? Asked of the
  // switcher itself so the ⋮ menu below cannot disagree with its only in-app row.
  const canSwitchFamily = useCanSwitchFamily();
  // The mobile ⋮ menu's open state. React owns it so the button can report
  // aria-expanded truthfully; the shared Dropdown supplies Escape, click-outside
  // and role="menu", which the previous hand-rolled classList toggle did not.
  const [authMenuOpen, setAuthMenuOpen] = useState(false);
  const authMenuTriggerRef = useRef<HTMLButtonElement>(null);
  // Closing must hand focus back to the ⋮ button. Escape reaches BOTH this menu
  // and the Switch family dialog (two document listeners), so the menu unmounts
  // the dialog and the dialog's own focus-restore then targets a node being
  // removed — measured: focus landed on <body>, and the next Tab restarted from
  // the top of this very long page. Sending it back here is what a keyboard user
  // expects anyway, and it covers the after-switch path too.
  const closeAuthMenu = () => {
    setAuthMenuOpen(false);
    authMenuTriggerRef.current?.focus();
  };

  // Filter states
  const [programTypeFilter, setProgramTypeFilter] = useState<ProgramTypeFilter>('all');
  const [availabilityFilter, setAvailabilityFilter] = useState<AvailabilityFilter>('all');
  const [locationFilter, setLocationFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [classCategoryFilter, setClassCategoryFilter] = useState<string>('all');

  // Event tickets (U4): the Events chip shows only when the gym has a
  // published ticketed event. ?tab=events (the event page's back link) opens it.
  const ticketedEvents = usePublicTicketedEvents(gymSlug, FEATURE_FLAGS.ENABLE_EVENT_TICKETS);
  useEffect(() => {
    if (ticketedEvents.length === 0 || typeof window === 'undefined') return;
    if (new URLSearchParams(window.location.search).get('tab') === EVENTS_CATALOG_TAB) setProgramTypeFilter('events');
  }, [ticketedEvents.length]);
  const [groupFilter, setGroupFilter] = useState<string>('all');
  const [catalogGroups, setCatalogGroups] = useState<CatalogClassGroup[]>([]);

  // Check authentication status. Besides the initial server-validated read, we
  // re-check when auth state changes so the header reflects the signed-in parent
  // even when the session lands a moment later — in the CheerVue mobile app the
  // catalog opens in a webview where the session is set asynchronously from the
  // URL token by the programs layout, which can resolve just after this first runs.
  useEffect(() => {
    let active = true;

    const checkAuth = async () => {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!active) return;
      setUser(authUser ? { email: authUser.email || '', id: authUser.id } : null);
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
        // Defer so we don't issue another Supabase call from inside the callback.
        setTimeout(() => checkAuth(), 0);
      }
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  // Fetch organization info
  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        const response = await fetch(`/api/public/organizations/${gymSlug}`);
        if (!response.ok) {
          // GH #2588: only a 404 means this gym really does not exist. Any other
          // status is the lookup failing, and a family looking at a real gym
          // should be told to try again — not that the gym is gone.
          if (response.status === 404) {
            // A mistyped or truncated link is not a failure worth alerting on.
            logger.warn('Gym slug not found:', { gymSlug });
            setError(GYM_NOT_FOUND_SCREEN);
          } else {
            logger.error('Organization lookup failed:', { gymSlug, status: response.status });
            setError(GYM_LOOKUP_UNAVAILABLE_SCREEN);
          }
          return;
        }
        const data = await response.json();
        setOrganization(data.organization);
      } catch (err) {
        // A thrown fetch (offline, DNS, aborted) is never evidence the gym is
        // missing, so it gets the try-again screen too (#2588).
        logger.error('Organization lookup failed:', err);
        setError(GYM_LOOKUP_UNAVAILABLE_SCREEN);
      }
    };

    fetchOrganization();
  }, [gymSlug, retryCount]);

  // Fetch programs
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/public/programs/browse?gymSlug=${gymSlug}`);
        if (!response.ok) {
          throw new Error('Failed to fetch programs');
        }
        const data = await response.json();
        
        setGymTimeZone(data.organization?.gym_timezone || DEFAULT_SERVER_TIMEZONE);
        setRegistrationLockNotice(data.registration_locked_notice === true);
        setLockedLocationNames(
          Array.isArray(data.registration_locked_locations)
            ? data.registration_locked_locations
                .map((loc: { name?: string }) => loc?.name)
                .filter((name: unknown): name is string => typeof name === 'string' && name.length > 0)
            : []
        );
        setCatalogGroups(Array.isArray(data.catalog_groups) ? data.catalog_groups : []);
        setPrograms(data.programs);
        setFilteredPrograms(data.programs);
      } catch (err) {
        logger.error('Failed to fetch programs:', err);
        setError({
          heading: "We couldn't load this gym's programs",
          message: 'Please try again in a moment.',
        });
      } finally {
        setLoading(false);
      }
    };

    if (organization) {
      fetchPrograms();
    }
  }, [gymSlug, organization]);

  // Extract unique class categories from programs (only categories that exist in data)
  const uniqueClassCategories = useMemo(() => {
    const categories = new Set<string>();
    programs.forEach(program => {
      if (program.program_type === 'class' && program.class_category) {
        categories.add(program.class_category);
      }
    });
    // Return in the same order as CLASS_CATEGORIES constant
    return CLASS_CATEGORIES.filter(cat => categories.has(cat));
  }, [programs]);

  // Reset class category filter when switching away from Classes tab
  useEffect(() => {
    if (programTypeFilter !== 'classes') {
      setClassCategoryFilter('all');
    }
  }, [programTypeFilter]);

  // #6005/#6010: the gym's groups that hold one of this tab's programs. Each
  // tab shows only those headings; the group filter needs 2 or more.
  const groupsOnTab = useMemo(
    () => groupsInUse(programs.filter((p) => matchesProgramTab(p, programTypeFilter)), catalogGroups),
    [programs, catalogGroups, programTypeFilter],
  );
  const showGroupFilter = groupsOnTab.length >= 2;

  useEffect(() => {
    setGroupFilter('all');
  }, [programTypeFilter]);

  useEffect(() => {
    if (!showGroupFilter) {
      setGroupFilter('all');
    }
  }, [showGroupFilter]);

  // Extract unique locations from programs
  const uniqueLocations = useMemo(() => {
    const locationMap = new Map();
    programs.forEach(program => {
      if (program.location) {
        locationMap.set(program.location.id, program.location);
      }
      if (program.schedule_locations) {
        program.schedule_locations.forEach(loc => {
          locationMap.set(loc.id, loc);
        });
      }
    });
    return Array.from(locationMap.values());
  }, [programs]);

  // Apply filters
  useEffect(() => {
    let filtered = [...programs];

    // Program type filter
    filtered = filtered.filter((p) => matchesProgramTab(p, programTypeFilter));

    // Availability filter
    if (availabilityFilter !== 'all') {
      filtered = filtered.filter((p) => {
        // Evaluation filter: only show programs requiring evaluations
        if (availabilityFilter === 'tryouts') {
          return p.registration_type === 'evaluation_required';
        }
        
        // For spot-based filters (Open/Limited/Waitlist), exclude evaluation programs
        // Evaluation programs should only appear in "Tryouts Available" filter
        if (p.registration_type === 'evaluation_required') {
          return false;
        }
        
        // Drop-in filter: show programs that allow drop-in sessions
        if (availabilityFilter === 'drop_in') {
          return isDropInOffered(p);
        }

        // Regular spot-based filtering for direct enrollment programs. Open
        // gyms classify by explicit staff intent only — their lifetime
        // spots_remaining arrives as null (capacity is per session), so a
        // busy open gym stays in the "open" bucket and never lands under
        // Waitlist. Regular classes keep numeric spots and behave exactly as
        // before.
        const gating = getPublicEnrollmentGating({
          isOpenGym: p.program_type === 'class' && (p as any).is_open_gym === true,
          enrollmentStatus: p.enrollment_status,
          spotsRemaining: p.spots_remaining,
          classOffer: p.program_type === 'class' ? p : null,
        });
        if (availabilityFilter === 'open') {
          return p.enrollment_status === 'open' && !gating.closed &&
            (typeof p.spots_remaining !== 'number' || p.spots_remaining > 5);
        } else if (availabilityFilter === 'limited') {
          return p.enrollment_status === 'open' && !gating.closed &&
            typeof p.spots_remaining === 'number' &&
            p.spots_remaining <= 5 && p.spots_remaining > 0;
        } else if (availabilityFilter === 'waitlist') {
          return gating.waitlistOnly;
        }
        
        return true;
      });
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          p.description?.toLowerCase().includes(query) ||
          p.instructor_name?.toLowerCase().includes(query)
      );
    }

    // Location filter
    if (locationFilter !== 'all') {
      filtered = filtered.filter(p => {
        // Check direct location (camps, teams)
        if (p.location?.id === locationFilter) return true;

        // Check schedule locations (classes)
        if (p.schedule_locations?.some(loc => loc.id === locationFilter)) return true;

        return false;
      });
    }

    // Class category filter (only applies when viewing classes)
    if (classCategoryFilter !== 'all') {
      filtered = filtered.filter(p => p.class_category === classCategoryFilter);
    }

    // Group filter (#6005): only the classes in the picked group
    if (groupFilter !== 'all') {
      const groupIds = new Set(catalogGroups.map((g) => g.id));
      filtered = filtered.filter((p) => groupIdOf(p, groupIds) === groupFilter);
    }

    setFilteredPrograms(filtered);
  }, [programs, programTypeFilter, availabilityFilter, searchQuery, locationFilter, classCategoryFilter, groupFilter, catalogGroups]);

  const catalogSections = useMemo(
    () => arrangeCatalogSections(filteredPrograms, {
      groups: catalogGroups,
      inUse: groupsOnTab,
      moreTitle: MORE_TITLE_BY_TAB[programTypeFilter] ?? MORE_PROGRAMS_TITLE,
      todayStr: getGymNowParts(gymTimeZone).dateStr,
    }),
    [filteredPrograms, catalogGroups, groupsOnTab, programTypeFilter, gymTimeZone],
  );

  const getProgramTypeLabel = (type: string) => {
    const labels = {
      class: 'Classes',
      classes: 'Classes',
      open_gym: 'Open Gym',
      tryouts: 'Tryouts',
      private_lessons: 'Private Lessons'
    };
    return labels[type as keyof typeof labels] || type;
  };

  const getRegistrationCTA = (program: Program) => {
    if (program.registration_type === 'evaluation_required') {
      return {
        text: 'Register',
        icon: 'assignment' as const,
        style: { color: brandTextColor(primaryColor) }
      };
    }
    // Default: direct enrollment
    return {
      text: 'View Details',
      icon: 'arrow_forward' as const,
      style: { color: brandTextColor(primaryColor) }
    };
  };

  const getAvailabilityBadge = (
    program: Program,
    closedStatus: { icon: string; label: string; testId?: string } | null
  ) => {
    if (closedStatus) {
      return (
        <span
          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full"
          data-testid={closedStatus.testId ?? 'closed-status-label'}
        >
          <GoogleMaterialIcon name={closedStatus.icon} size="sm" />
          {closedStatus.label}
        </span>
      );
    }

    // Private lessons use booking system, not enrollment - always show as available
    if (program.program_type === 'private_lessons') {
      return (
        <span 
          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full"
          style={{ backgroundColor: `${primaryColor}1a`, color: brandTextColor(primaryColor, 10) }}
        >
          <GoogleMaterialIcon name="calendar_month" size="sm" />
          Available to Book
        </span>
      );
    }

    // For evaluation programs, show capacity-based badges
    if (program.registration_type === 'evaluation_required') {
      const now = new Date();
      // For comparison, use actual timestamps
      const registrationOpens = program.registration_opens_at ? new Date(program.registration_opens_at) : null;
      const registrationCloses = program.registration_closes_at ? new Date(program.registration_closes_at) : null;
      
      // Registration hasn't opened yet - show opens date
      if (registrationOpens && now < registrationOpens) {
        // For display, use timezone-safe parsing to show the date as stored in database
        const displayDate = parseDateLocal(program.registration_opens_at!);
        return (
          <span 
          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full"
          style={{ backgroundColor: `${primaryColor}1a`, color: brandTextColor(primaryColor, 10) }}
        >
            <GoogleMaterialIcon name="event_available" size="sm" />
            Opens {displayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        );
      }
      
      // Registration has closed
      if (registrationCloses && now > registrationCloses) {
        return (
          <span
            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full"
            data-testid="closed-status-label"
          >
            <GoogleMaterialIcon name="event_busy" size="sm" />
            Registration Closed
          </span>
        );
      }
      
      // Check if tryout event is full
      if (program.spots_remaining === 0) {
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-gray-50 rounded-full">
            <GoogleMaterialIcon name="event_busy" size="sm" />
            Evaluation Event Full
          </span>
        );
      }
      
      // Check if limited spots remaining (1-5)
      if (program.spots_remaining <= 5 && program.spots_remaining > 0) {
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-orange-700 bg-orange-50 rounded-full">
            <GoogleMaterialIcon name="warning" size="sm" />
            {program.spots_remaining} evaluation spots left
          </span>
        );
      }
      
      // Check if many spots available (6-998)
      if (program.spots_remaining > 5 && program.spots_remaining < 999) {
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-50 rounded-full">
            <GoogleMaterialIcon name="check_circle" size="sm" />
            Evaluations Open - {program.spots_remaining} spots
          </span>
        );
      }
      
      // Unlimited capacity or no event scheduled yet
      return (
        <span 
          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full"
          style={{ backgroundColor: `${primaryColor}1a`, color: brandTextColor(primaryColor, 10) }}
        >
          <GoogleMaterialIcon name="assignment" size="sm" />
          Evaluation Required
        </span>
      );
    }

    // For direct enrollment programs, show spot-based availability. Shared
    // predicate — only explicit staff intent puts the Waitlist badge on an
    // open gym; its null lifetime spots never do.
    const gating = getPublicEnrollmentGating({
      isOpenGym: program.program_type === 'class' && program.is_open_gym === true,
      enrollmentStatus: program.enrollment_status,
      spotsRemaining: program.spots_remaining,
    });
    if (gating.waitlistOnly) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-yellow-700 bg-yellow-50 rounded-full">
          <GoogleMaterialIcon name="hourglass_empty" size="sm" />
          Waitlist Only
        </span>
      );
    }

    // No numeric spots = no spots badge. This is what open gyms hit (null
    // spots): without it, `null <= 5` is true and renders a broken " spots
    // left" pill.
    if (typeof program.spots_remaining !== 'number') return null;

    if (program.spots_remaining <= 5) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-orange-700 bg-orange-50 rounded-full">
          <GoogleMaterialIcon name="warning" size="sm" />
          {program.spots_remaining} spots left
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-50 rounded-full">
        <GoogleMaterialIcon name="check_circle" size="sm" />
        {program.spots_remaining} spots available
      </span>
    );
  };

  // Clear all filters
  const clearAllFilters = () => {
    setProgramTypeFilter('all');
    setAvailabilityFilter('all');
    setLocationFilter('all');
    setClassCategoryFilter('all');
    setGroupFilter('all');
    setSearchQuery('');
  };

  // Active filters count
  const activeFiltersCount = [
    programTypeFilter !== 'all',
    availabilityFilter !== 'all',
    locationFilter !== 'all',
    classCategoryFilter !== 'all',
    groupFilter !== 'all',
    searchQuery !== ''
  ].filter(Boolean).length;

  // Notice copy. Both sentences state only what the browse route computed. The title
  // names the LOCATION, never the gym: at a gym whose other location still sells, a
  // gym-wide sentence would be false. When no location name came back, the fallback
  // names nothing rather than guessing, and still avoids a gym-wide claim.
  const lockedLocationsPhrase =
    lockedLocationNames.length === 0
      ? null
      : lockedLocationNames.length === 1
        ? lockedLocationNames[0]
        : `${lockedLocationNames.slice(0, -1).join(', ')} and ${lockedLocationNames[lockedLocationNames.length - 1]}`;
  const registrationLockTitle = lockedLocationsPhrase
    ? `Registrations at ${lockedLocationsPhrase} aren't available right now`
    : "Some registrations aren't available right now";

  // Use fallback color for other elements, but button will use brand color only when loaded
  const primaryColor = organization?.branding?.primary_color || DEFAULT_PRIMARY_COLOR;
  const brandColorReady = !!organization?.branding?.primary_color;

  // Program card cover: when a program has no photo we show a soft tinted panel.
  // A gym that picked its own color gets a tint of it (--brand-primary, set on
  // the wrapper below) so the catalog reads as theirs. A gym still on the
  // untouched signup default never chose that color, so tinting the panel with
  // it would put a color on their page they never asked for — those fall back
  // to neutral gray, which reads as "no photo yet" instead of a brand choice.
  const hasCustomBrandColor =
    !!organization?.branding?.primary_color &&
    organization.branding.primary_color.trim().toLowerCase() !== DEFAULT_PRIMARY_COLOR.toLowerCase();
  const coverPlaceholderStyle: React.CSSProperties = hasCustomBrandColor
    ? {
        backgroundImage:
          'linear-gradient(to bottom right, color-mix(in srgb, var(--brand-primary) 12%, #fff), color-mix(in srgb, var(--brand-primary) 22%, #fff))',
      }
    : { backgroundImage: 'linear-gradient(to bottom right, #f3f4f6, #e5e7eb)' };
  const coverPlaceholderIconStyle: React.CSSProperties = {
    color: hasCustomBrandColor ? 'color-mix(in srgb, var(--brand-primary) 55%, #fff)' : '#9ca3af',
  };

  // FAMILY / GUARDIAN ACCESS gate (catalog CTA). True ONLY when the flag is on, a
  // specific active family is resolved, that family belongs to the gym being
  // browsed (the hook's org-match guard), AND the viewer cannot manage that
  // family's enrollments — so it is inert for flag-off, guests, primary
  // guardians, and any cross-org mismatch. When true, the Register / Enroll CTA
  // renders disabled with a short reason instead of a working link. The four
  // preconditions live in the shared useFamilyAthleteGate hook, not here.
  const blockFamilyRegistration = familyGate.blockAllInActiveFamily;

  // Category configuration for chips (Classes = unified class_programs)
  const categories = [
    { id: 'all', label: 'All Programs', icon: 'apps' },
    { id: 'classes', label: 'Classes', icon: 'sports_gymnastics' },
    { id: 'open_gym', label: 'Open Gym', icon: 'warehouse' },
    { id: 'private_lessons', label: 'Private Lessons', icon: 'group' },
    { id: 'tryouts', label: 'Tryouts', icon: 'assignment' },
    ...(ticketedEvents.length > 0 ? [{ id: 'events', label: 'Events', icon: 'confirmation_number' }] : []),
  ];

  // A reload part-way down would otherwise land in the crawler summary under the skeleton.
  useKeepScrollAcrossReload((organization !== null && !loading) || error !== null);

  if (error) {
    const canRetry = error !== GYM_NOT_FOUND_SCREEN;

    // THE SERVER ALREADY WROTE THIS GYM'S CATALOG, SO AN ERROR SCREEN WOULD BE
    // DELETING CONTENT THE PAGE ALREADY HAS. Two readers make this load-bearing
    // rather than tidy:
    //
    //  - GOOGLEBOT RENDERS. It fetches the page, gets the summary, then runs
    //    the mount fetch below — which asks `/api/public/...`, and robots.txt
    //    disallows `/api/`. Google's renderer refuses that request, this branch
    //    catches it, and without this guard the DOM Google indexes is "we
    //    couldn't load this page" with no program, price or schedule in it. The
    //    whole unit's value would reach every crawler EXCEPT the one that
    //    matters most, and no server-render test could see it.
    //  - A PERSON whose connection dropped is better served by the real list of
    //    programs plus a Try again than by a screen that throws it away.
    //
    // Retry still sits below it, so nothing is lost; only the heading goes, so
    // the page keeps exactly one `<h1>` — the summary's.
    if (serverSummary) {
      return (
        <div className="min-h-screen bg-white">
          {serverSummary}
          {/*
            The message always shows, the button only when retrying can help.
            A cached copy of this page under a slug the gym has since changed
            reaches the not-found branch: without the message that visitor gets
            a bare program list with no filters, no sign-up buttons and nothing
            telling them why.
          */}
          <div className="mx-auto max-w-7xl px-4 pb-10 sm:px-6 lg:px-8">
            <p className="text-gray-600">{error.message}</p>
            {canRetry && (
              <Button
                variant="outline"
                className="mt-3"
                onClick={() => {
                  setError(null);
                  setRetryCount((count) => count + 1);
                }}
              >
                Try again
              </Button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-6">
        <div className="text-center max-w-md mx-auto">
          <GoogleMaterialIcon name="error" size="lg" className="text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{error.heading}</h1>
          <p className="text-gray-600">{error.message}</p>
          {canRetry && (
            <Button
              variant="outline"
              className="mt-6"
              onClick={() => {
                setError(null);
                setRetryCount((count) => count + 1);
              }}
            >
              Try again
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Show full-page skeleton until organization data loads.
  // Prevents half-styled page (blue fallback colors, "Loading..." text,
  // unstyled buttons) on slower connections.
  if (!organization) {
    const skeleton = (
      <div className="min-h-screen bg-white">
        {/* Skeleton mirrors the real header's three bands (utility strip,
            centered logo, category tabs) so the page does not jump when the
            gym's data lands. */}
        <header className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between gap-3 h-16 animate-pulse">
              <div className="hidden md:block h-4 w-40 bg-gray-200 rounded" />
              <div className="hidden md:flex items-center gap-3">
                <div className="h-10 w-48 bg-gray-200 rounded-lg" />
                <div className="h-10 w-24 bg-gray-200 rounded-lg" />
                <div className="h-10 w-36 bg-gray-200 rounded-lg" />
              </div>
              <div className="flex md:hidden items-center gap-2 flex-1">
                <div className="h-10 flex-1 bg-gray-200 rounded-lg" />
                <div className="h-10 w-10 bg-gray-200 rounded-full flex-shrink-0" />
              </div>
            </div>
          </div>
        </header>
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-center py-4 md:py-6 animate-pulse">
              <div className="h-24 md:h-40 w-64 bg-gray-200 rounded" />
            </div>
          </div>
        </div>
        <div className="border-b border-gray-200 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Same scroller as the real chip row: without it the placeholders
                overflow the body and the loading page scrolls sideways. */}
            <div className="overflow-x-auto scrollbar-hide py-3">
              <div className="flex items-center gap-2 w-max md:mx-auto animate-pulse">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-9 w-28 bg-gray-200 rounded-full flex-shrink-0" />
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse mb-6">
            <div className="h-7 w-48 bg-gray-200 rounded mb-2" />
            <div className="h-4 w-32 bg-gray-200 rounded" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white rounded-2xl shadow-card-lift border border-transparent overflow-hidden animate-pulse">
                <div className="w-full h-48 bg-gray-200" />
                <div className="p-6 space-y-4">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 rounded w-full" />
                  <div className="h-3 bg-gray-200 rounded w-5/6" />
                  <div className="space-y-2 pt-4">
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                    <div className="h-3 bg-gray-200 rounded w-2/3" />
                  </div>
                  <div className="h-10 bg-gray-200 rounded w-full mt-4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );

    // The server already wrote this gym's catalog into the first response. It
    // stays in the page for crawlers, below the skeleton, so a person never
    // sees it flash up before the interactive catalog replaces it.
    if (serverSummary) {
      return <SummaryBelowPlaceholder placeholder={skeleton} summary={serverSummary} />;
    }

    return skeleton;
  }

  // FAMILY / GUARDIAN ACCESS — shared disabled-CTA shown in place of the working
  // Register / Enroll link when the viewer is acting as a family they cannot
  // register for. Reused by both card CTA paths (tryout + regular) so the copy
  // and styling stay in one place (DRY). Same sentence as the picker, the
  // private-lesson page, and the server denial.
  const familyBlockedCta = (
    <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-600 text-center">
      <GoogleMaterialIcon name="lock" size="xs" className="mb-1 block mx-auto text-gray-400" />
      {CANNOT_REGISTER_ATHLETE_MESSAGE}
    </div>
  );

  // GH #883 U5 (R8): the gym is over its Free plan cap with no grace left —
  // NEW-athlete registration is closed. Browsing always works, and a
  // signed-in parent's existing athletes stay registrable (the downstream
  // athlete picker and the server-side guard handle that split), so the
  // card's "Not taking new athletes online" label shows only to signed-OUT
  // visitors, whose only path in is registering a new family. Reopens on the next
  // load after the gym subscribes (flag is per-request, never cached).
  const capBlocksNewRegistration = organization?.roster_cap_blocked === true && !user;

  return (
    <div
      className="min-h-screen bg-white"
      style={{
        '--color-brand-gradient-start': primaryColor,
        '--color-brand-gradient-end': primaryColor,
        '--brand-primary': primaryColor,
      } as React.CSSProperties}
    >
      {/* Utility strip. Stays pinned as the page scrolls so search and the
          account buttons are always reachable — the logo band below is the part
          that scrolls away, since pinning a 160px logo would cover a third of a
          phone screen. Fixed 64px tall so the category tabs can pin directly
          beneath it. */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-3 h-16">
            {/* Gym name sits quietly to the left so the centered logo below owns
                the band. Skipped when there is no logo, since the fallback
                heading in the band already names the gym. */}
            {organization?.logo_url && (
              <p className="hidden md:block text-sm text-gray-600 truncate min-w-0">
                {organization.name}
              </p>
            )}

            {/* Search + Auth - Desktop: search next to buttons */}
            <div className="hidden md:flex items-center gap-3 flex-1 min-w-0 max-w-xl ml-auto justify-end">
              <SearchField
                id="program-search"
                name="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search programs, classes, camps..."
                showClearButton={true}
                onClear={() => setSearchQuery('')}
                className="w-48 sm:w-56 flex-shrink-0"
              />
              {isMobileWebView ? (
                /* In-app: the family switcher still earns its place (it changes
                   which household the catalog is gating against); the account
                   buttons do not. */
                familyContextMatchesThisGym && user ? <SwitchFamilyButton variant="header" /> : null
              ) : user ? (
                <>
                  {/* Family switcher: a multi-family parent can change which household
                      they are acting as right here, so the catalog (and its
                      registration gating) reflects that family without a trip back to
                      the dashboard. Renders nothing for single-family / flag-off users. */}
                  {familyContextMatchesThisGym && <SwitchFamilyButton variant="header" />}
                  {/* Names the account this page is signed in as (#4897) — the
                      app can sign a browser sheet in from a link; see
                      SignedInAsBadge. */}
                  <PublicAccountMenu user={accountMenuUser} />
                </>
              ) : (
                <>
                  {/* Sign In is the primary (branded) CTA. Most cheer parents
                      arriving here already have an account. Both buttons stay in
                      the same tab so the post-signin redirect lands back here, and
                      both preserve the current query string (utm params, filters). */}
                  <button
                    onClick={() => {
                      const target = window.location.pathname + window.location.search;
                      window.location.href = `/signin?redirectTo=${encodeURIComponent(target)}`;
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors hover:brightness-95"
                    style={{ backgroundColor: primaryColor, color: readableTextOn(primaryColor) }}
                  >
                    <GoogleMaterialIcon name="login" size="sm" />
                    Sign In
                  </button>
                  <button
                    onClick={() => {
                      const target = window.location.pathname + window.location.search;
                      const params = new URLSearchParams({ redirectTo: target });
                      if (organization?.id) {
                        params.set('organizationId', organization.id);
                        if (organization?.name) params.set('organizationName', organization.name);
                      }
                      window.location.href = `/register?${params.toString()}`;
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <GoogleMaterialIcon name="person_add" size="sm" />
                    Create Account
                  </button>
                </>
              )}
            </div>

            {/* Mobile: Search next to menu */}
            <div className="flex md:hidden items-center gap-2 flex-1 min-w-0">
              <SearchField
                id="program-search-mobile"
                name="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search programs, classes, camps..."
                showClearButton={true}
                onClear={() => setSearchQuery('')}
                className="flex-1 min-w-0"
              />
            </div>

            {/* Mobile Ellipse Menu for Auth Buttons. In-app the menu holds only the
                family switcher, so it disappears entirely when that row would not
                render — an empty dropdown is worse than no menu button. The row
                only renders for a multi-family parent, so the gate asks the
                switcher (canSwitchFamily), not just whether someone is signed in
                at this gym: a single-family parent left the button opening an
                empty sheet. `user` stays in the conjunction as a belt: it and
                the family context resolve from independent async reads, and
                neither ordering is pinned. */}
            <div
              className={`relative md:hidden flex-shrink-0${
                isMobileWebView && !(familyContextMatchesThisGym && user && canSwitchFamily)
                  ? ' hidden'
                  : ''
              }`}
            >
              <button
                ref={authMenuTriggerRef}
                type="button"
                onClick={() => setAuthMenuOpen((open) => !open)}
                className="dropdown-toggle p-2 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Menu"
                aria-haspopup="menu"
                aria-expanded={authMenuOpen}
              >
                <GoogleMaterialIcon name="more_vert" size="md" />
              </button>

              {/* Dropdown Menu — the shared primitive, which brings Escape,
                  close-on-tap-outside, role="menu" and a viewport-fit cap. It
                  keeps the same right-aligned placement this menu already had.
                  Its own z-index only ever competes with its siblings inside the
                  strip: the strip's sticky + z-50 makes a stacking context, and
                  that is what keeps the open menu above the category tabs. */}
              <Dropdown
                isOpen={authMenuOpen}
                onClose={closeAuthMenu}
                triggerRef={authMenuTriggerRef as React.RefObject<HTMLElement>}
                className="w-48 py-2"
              >
                {isMobileWebView ? (
                  familyContextMatchesThisGym && user && canSwitchFamily ? (
                    <SwitchFamilyButton
                      variant="menu"
                      onSwitched={closeAuthMenu}
                    />
                  ) : null
                ) : user ? (
                  <>
                    {/* THE PHONE'S COPY OF THE ACCOUNT NAME (#4897). The header
                        block above is `hidden md:flex`, so on a phone — which is
                        where the app's browser sheet actually is — this menu is
                        the only place the name can appear. The rows are the
                        same ones the desktop avatar menu shows. */}
                    <PublicAccountMenuItems
                      user={accountMenuUser}
                      showFamilySwitcher={familyContextMatchesThisGym}
                      onClose={closeAuthMenu}
                    />
                  </>
                ) : (
                  <>
                    {/* Mobile menu mirrors the desktop hierarchy: Sign In is the
                        primary solid-brand CTA (matching desktop), Create Account is
                        secondary. Same-tab for both to preserve the post-signin
                        redirect target. Cheer parents are overwhelmingly on phones,
                        so this surface gets the same visual priority as desktop. */}
                    <button
                      onClick={() => {
                        const target = window.location.pathname + window.location.search;
                        window.location.href = `/signin?redirectTo=${encodeURIComponent(target)}`;
                      }}
                      className="w-full px-4 py-3 text-left text-sm flex items-center gap-2 font-semibold text-white hover:brightness-95 transition-all"
                      style={{ backgroundColor: primaryColor, color: readableTextOn(primaryColor) }}
                     role="menuitem"
                    >
                      <GoogleMaterialIcon name="login" size="sm" />
                      Sign In
                    </button>
                    <button
                      onClick={() => {
                        const target = window.location.pathname + window.location.search;
                        const params = new URLSearchParams({ redirectTo: target });
                        if (organization?.id) {
                          params.set('organizationId', organization.id);
                          if (organization?.name) params.set('organizationName', organization.name);
                        }
                        window.location.href = `/register?${params.toString()}`;
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      role="menuitem"
                    >
                      <GoogleMaterialIcon name="person_add" size="sm" />
                      Create Account
                    </button>
                  </>
                )}
              </Dropdown>
            </div>
          </div>
        </div>
      </header>

      {/* Logo band. 160px tall on desktop with the width left free, so a tall
          badge, a wide wordmark, and a banner logo all sit correctly.
          object-contain keeps a wide logo from stretching. This band scrolls
          away; the strip above and the tabs below stay pinned. */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center py-4 md:py-6">
            {organization?.logo_url && !logoFailed ? (
              <>
                {/* The logo carries the gym name visually, so the name is given
                    to screen readers here rather than left off the page. */}
                <h1 className="sr-only">{organization.name}</h1>
                <img
                  src={organization.logo_url}
                  alt=""
                  onError={() => setLogoFailed(true)}
                  className="h-24 md:h-40 w-auto max-w-full object-contain"
                />
              </>
            ) : (
              <div className="text-center">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                  {organization?.name || 'Loading...'}
                </h1>
                <p className="text-sm text-gray-600 mt-1">Discover Programs</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Category tabs pin directly beneath the 64px utility strip, so the
          filters stay reachable once the logo band scrolls away.
          Requirement summary: mobile catalog category chips should scroll edge-to-edge without fade overlays covering labels or touch targets. */}
      <div className="sticky top-16 z-40 border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto">
          <div data-testid="program-category-tabs-shell" className="relative">
            <div className="overflow-x-auto scrollbar-hide px-4 py-3 sm:px-6 lg:px-8">
              {/* w-max + mx-auto centers the row on desktop but collapses to
                  left-aligned as soon as the chips outgrow it, so the leading
                  chip can never end up stranded past the start edge. */}
              <div
                data-testid="program-category-tabs"
                className="flex items-center gap-2 w-max md:mx-auto"
              >
                {categories.map((category) => {
                  const isActive = programTypeFilter === category.id;
                  // Only apply brand color when organization data is loaded to prevent flash
                  // Use neutral gray while organization is loading
                  const buttonStyle = isActive
                    ? brandColorReady && organization?.branding?.primary_color
                      ? { backgroundColor: organization.branding.primary_color, color: readableTextOn(organization.branding.primary_color) }
                      : { backgroundColor: '#6B7280' } // Neutral gray while loading
                    : {};
                  
                  return (
                    <button
                      key={category.id}
                      data-testid={`program-category-${category.id}`}
                      aria-pressed={isActive}
                      onClick={() => setProgramTypeFilter(category.id as ProgramTypeFilter)}
                      // Height floor so "All Programs", the one chip with no
                      // icon, matches the rest — icons render 24px against a
                      // 20px text line, which otherwise leaves it 4px shorter.
                      // A floor rather than a fixed height: the scroller around
                      // this row hides its scrollbar, so a chip that cannot grow
                      // would clip its own label when text is scaled up.
                      className={`flex items-center gap-2 min-h-10 px-4 py-2 rounded-full text-sm font-light whitespace-nowrap transition-all ${
                        isActive
                          ? 'text-white shadow-md'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      style={buttonStyle}
                    >
                      {category.id !== 'all' && (
                        <GoogleMaterialIcon name={category.icon} size="sm" />
                      )}
                      {category.label}
                    </button>
                  );
                })}
                {/* Merch Store — a door, not a filter: it leaves for the gym's
                    store page, so it renders as a link, carries no aria-pressed,
                    and sits last so the filter chips stay a contiguous set. Shown
                    only when the gym's store is open (merch_store_open, fails
                    closed) and never inside the app's webview — the app has its
                    own Merch Store entry in the More menu. */}
                {organization?.merch_store_open && !isMobileWebView && (
                  <a
                    href={`/store/${gymSlug}`}
                    data-testid="program-category-merch-store"
                    className="flex items-center gap-2 min-h-10 px-4 py-2 rounded-full text-sm font-light whitespace-nowrap transition-all bg-gray-100 text-gray-700 hover:bg-gray-200"
                  >
                    <GoogleMaterialIcon name="storefront" size="sm" />
                    Merch Store
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {programTypeFilter === 'events' && (
        <PublicEventsList gymSlug={gymSlug} events={ticketedEvents} primaryColor={organization?.branding?.primary_color ?? null} />
      )}
      {/* Hidden, not unmounted, on the Events tab so the program filters keep their state. */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" hidden={programTypeFilter === 'events'}>
        {/* Registration lock notice, with `role="status"` — KTD8 picks the role by
            how the notice ARRIVES, and this one arrives late. Observed by rendering
            the real component: the header, gym hero, category chips, results header,
            filter bar and six skeleton cards all paint as soon as the organization
            fetch lands, while `loading` is still true. The notice is gated on
            `!loading`, so it is inserted into an already-painted page one fetch
            later — a post-load change, which is exactly the case KTD8 gives a polite
            live region. Without it a screen-reader user who has already moved past
            the top of the results is never told the registrations are unavailable.
            The role sits on this always-mounted wrapper, not on the Alert, so the
            region exists before the notice arrives in it. */}
        <div role="status">
          {!loading && registrationLockNotice && (
            <Alert
              variant="warning"
              title={registrationLockTitle}
              /* "You can still look around" is only true when there is something to
                 look at. This page suppresses its own empty state when the notice is
                 up, so on a catalog listing nothing the notice IS the page — and that
                 sentence would invite a parent to browse a blank screen. The title
                 alone is true in both states. */
              message={filteredPrograms.length > 0 ? 'You can still look around.' : undefined}
              className="mb-6"
              data-testid="registration-locked-notice"
            />
          )}
        </div>

        {/* Results Header and Filters */}
        <div className="mb-6">
          {/* Header with Filters - Desktop: side-by-side, Mobile: stacked */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            {/* Left: Title and count */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {programTypeFilter === 'all' ? 'All Programs' : getProgramTypeLabel(programTypeFilter)}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {filteredPrograms.length} {filteredPrograms.length === 1 ? 'program' : 'programs'} found
              </p>
            </div>

            {/* Right: Compact filter dropdowns - Desktop inline, Mobile stacked */}
            <div className="flex flex-col md:flex-row gap-3 md:items-center">
              {/* Group Filter (#6005) - the gym's own groups, when it uses 2 or more */}
              {showGroupFilter && (
                <div className="w-full md:w-52">
                  <SelectField
                    id="group-filter"
                    name="groupFilter"
                    value={groupFilter}
                    onChange={(value) => setGroupFilter(value)}
                    options={[
                      { value: 'all', label: 'All Groups' },
                      ...groupsOnTab.map(group => ({
                        value: group.id,
                        label: group.name
                      }))
                    ]}
                    label=""
                    className="w-full"
                  />
                </div>
              )}

              {/* Class Category Filter - only shown when Classes tab is active */}
              {programTypeFilter === 'classes' && uniqueClassCategories.length > 1 && (
                <div className="w-full md:w-52">
                  <SelectField
                    id="class-category-filter"
                    name="classCategoryFilter"
                    value={classCategoryFilter}
                    onChange={(value) => setClassCategoryFilter(value)}
                    options={[
                      { value: 'all', label: 'All Categories' },
                      ...uniqueClassCategories.map(cat => ({
                        value: cat,
                        label: cat
                      }))
                    ]}
                    label=""
                    className="w-full"
                  />
                </div>
              )}

              {/* Availability Filter Dropdown */}
              <div className="w-full md:w-48">
                <SelectField
                  id="availability-filter"
                  name="availabilityFilter"
                  value={availabilityFilter}
                  onChange={(value) => setAvailabilityFilter(value as AvailabilityFilter)}
                  options={[
                    { value: 'all', label: 'All Availability' },
                    { value: 'open', label: 'Open' },
                    { value: 'limited', label: 'Limited Spots' },
                    { value: 'waitlist', label: 'Waitlist' },
                    { value: 'tryouts', label: 'Evaluations Available' },
                    { value: 'drop_in', label: 'Drop-in Available' }
                  ]}
                  label=""
                  className="w-full"
                />
              </div>

              {/* Location Filter Dropdown */}
              {uniqueLocations.length > 0 && (
                <div className="w-full md:w-48">
                  <SelectField
                    id="location-filter"
                    name="locationFilter"
                    value={locationFilter}
                    onChange={(value) => setLocationFilter(value)}
                    options={[
                      { value: 'all', label: 'All Locations' },
                      ...uniqueLocations.map(location => ({
                        value: location.id,
                        label: location.name
                      }))
                    ]}
                    label=""
                    className="w-full"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Program Grid */}
        {loading ? (
          /* Skeleton Loaders */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white rounded-2xl shadow-card-lift border border-transparent overflow-hidden animate-pulse">
                <div className="w-full h-48 bg-gray-200" />
                <div className="p-6 space-y-4">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 rounded w-full" />
                  <div className="h-3 bg-gray-200 rounded w-5/6" />
                  <div className="space-y-2 pt-4">
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                    <div className="h-3 bg-gray-200 rounded w-2/3" />
                  </div>
                  <div className="h-10 bg-gray-200 rounded w-full mt-4" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredPrograms.length === 0 ? (
          // With the lock notice up and no filters applied, "Try adjusting your
          // filters" and its Clear button are both false — there is nothing to
          // adjust. Let the notice be the whole content instead.
          registrationLockNotice && activeFiltersCount === 0 ? null : (
          <div className="text-center py-16">
            <GoogleMaterialIcon name="search_off" size={64} className="text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No programs found</h3>
            <p className="text-gray-600 mb-4">Try adjusting your filters or search query</p>
            {activeFiltersCount > 0 && (
              <Button variant="primary" onClick={clearAllFilters} style={{ color: readableTextOn(primaryColor) }}>
                Clear all filters
              </Button>
            )}
          </div>
          )
        ) : (
          <div className="space-y-10">
            {catalogSections.map((section) => (
              <section key={section.key} aria-label={section.title ?? undefined} data-testid="catalog-section">
                {section.title && (
                  <div className="flex items-baseline gap-2 mb-4 pb-2 border-b-2" style={{ borderColor: primaryColor }}>
                    <h3 className="text-xl font-bold text-gray-900">{section.title}</h3>
                    <span className="text-sm text-gray-600">
                      {section.programs.length} {programTypeFilter === 'classes'
                        ? (section.programs.length === 1 ? 'class' : 'classes')
                        : (section.programs.length === 1 ? 'program' : 'programs')}
                    </span>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {section.programs.map((program) => {
                  // Evaluation events use different URL structure (standalone, not programs)
                  // Private lessons go to details page first (shows pricing, packages, policies)
                  // Other programs go to detail page
                  const programHref = (program as any).is_tryout_event
                    ? (tryoutDetailPath(gymSlug, program.id) ?? `/programs/${gymSlug}`)
                    : program.program_type === 'private_lessons'
                    ? `/programs/${gymSlug}/private-lessons/${program.id}`
                    : `/programs/${gymSlug}/${program.program_type === 'class' ? 'class' : program.program_type}/${program.id}`;

                  // For tryout events, use the shared registration-status utility so the catalog
                  // agrees with the registration page and API guard. Without this, parents could
                  // click Register on an evaluation that already happened and hit a dead-end page.
                  const tryoutCheck = program.registration_type === 'evaluation_required' && (program as any).tryout_registration
                    ? isTryoutRegistrationOpen((program as any).tryout_registration, gymTimeZone)
                    : null;

                  // For fixed-term classes, "ended" = end_date before the gym's today. Same
                  // dead-end problem as expired tryouts otherwise.
                  const programEnded = isProgramEnded(program, getGymNowParts(gymTimeZone).dateStr);

                  // Why this card cannot be signed up for right now, if it cannot.
                  // Same order the old grey button used: lock first, so no later
                  // label can suggest a sign-up the gym cannot take.
                  const closedStatus: { icon: string; label: string; testId?: string } | null =
                    program.registration_locked && program.program_type !== 'private_lessons'
                      ? { icon: 'block', label: 'Not available right now', testId: 'registration-locked-pill' }
                      : (program as any).coming_soon
                        ? { icon: 'schedule', label: 'Coming Soon' }
                        : isEnrollmentClosed(program)
                          ? { icon: 'cancel', label: 'Enrollment Closed' }
                          : programEnded
                            ? { icon: 'event_busy', label: 'Class Ended' }
                            : tryoutCheck && !tryoutCheck.isOpen
                              ? tryoutCheck.reason === 'Registration has not opened yet'
                                ? {
                                    icon: 'schedule',
                                    label: program.registration_opens_at
                                      ? `Opens ${parseDateLocal(program.registration_opens_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                                      : 'Registration Opens Soon',
                                  }
                                : { icon: 'event_busy', label: 'Registration Closed' }
                              : null;

                  // Otherwise, why THIS viewer cannot sign up from here. Private
                  // lessons are exempt from the roster-limit and family labels, as
                  // they were from the old messages: their page gates booking itself.
                  const viewerStatus: { icon: string; label: string; testId?: string } | null =
                    selfServiceAccess === 'unconfirmed'
                      ? { icon: 'error', label: "Couldn't check your account" }
                      : selfServiceUnavailable
                        ? { icon: 'badge', label: "Staff accounts can't sign up" }
                        : isInvitedAthlete
                          ? { icon: 'family_restroom', label: 'Ask a parent to sign you up' }
                          : capBlocksNewRegistration && program.program_type !== 'private_lessons'
                            ? { icon: 'info', label: 'Not taking new athletes online' }
                            : blockFamilyRegistration && program.program_type !== 'private_lessons'
                              ? { icon: 'lock', label: 'Needs family access to sign up' }
                              : null;
                  const cardStatus = closedStatus ?? viewerStatus;

                  // Determine if registration is closed/unavailable
                  const isClosed = (() => {
                    if ((program as any).coming_soon) return true;
                    if (isEnrollmentClosed(program)) return true;
                    if (tryoutCheck) return !tryoutCheck.isOpen;
                    if (programEnded) return true;
                    return false;
                  })();

                  return (
                    <div
                      key={program.id}
                      className={`block bg-white rounded-2xl shadow-card-lift border border-transparent overflow-hidden ${
                        isClosed ? "grayscale-[0.2] opacity-95" : ""
                      }`}
                    >
                      {/* Cover Image */}
                      {program.cover_image_url ? (
                        <div className="relative w-full h-48 overflow-hidden" style={coverPlaceholderStyle}>
                          <img
                            src={program.cover_image_url}
                            alt={program.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="relative w-full h-48 flex items-center justify-center" style={coverPlaceholderStyle}>
                          <GoogleMaterialIcon
                            name={
                              program.program_type === 'team' && program.team_type === 'competitive_teams' ? 'emoji_events' :
                              program.program_type === 'team' && program.team_type === 'recreational_teams' ? 'sentiment_very_satisfied' :
                              // Open Gym U5 (R7): a distinct cover icon so the offering
                              // reads as "open floor time," not a curriculum class.
                              program.program_type === 'class' && program.is_open_gym ? 'warehouse' :
                              program.program_type === 'class' ? 'sports_gymnastics' :
                              program.program_type === 'private_lessons' ? 'group' :
                              'celebration'
                            }
                            size={96}
                            className="opacity-30"
                            style={coverPlaceholderIconStyle}
                          />
                        </div>
                      )}
                  
                      {/* Program Card Content */}
                      <div className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            {/* Gym Location — shown prominently above program name */}
                            {(program.location || (program.schedule_locations && program.schedule_locations.length > 0)) && (
                              <div className="flex items-center gap-1.5 text-sm font-semibold mb-1" style={{ color: brandTextColor(primaryColor) }}>
                                <GoogleMaterialIcon name="location_on" size="sm" />
                                {program.location
                                  ? program.location.name
                                  : program.schedule_locations?.map(loc => loc.name).join(', ')
                                }
                              </div>
                            )}
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                              {program.name}
                            </h3>
                            <div className="flex items-center gap-2 flex-wrap">
                              {/* Open Gym U5 (R7): a distinct identity badge so the
                                  offering reads as its own thing in the catalog. For
                                  open gyms this REPLACES the generic "Classes" type
                                  label; we also suppress the curriculum "category" and
                                  "Ongoing" (monthly) badges below, which don't apply to
                                  pay-per-visit open floor time. */}
                              {program.program_type === 'class' && program.is_open_gym ? (
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded"
                                  style={{ backgroundColor: `${primaryColor}1a`, color: brandTextColor(primaryColor, 10) }}
                                >
                                  <GoogleMaterialIcon name="warehouse" size="sm" />
                                  Open Gym
                                </span>
                              ) : (
                                <span
                                  className="inline-block px-2 py-1 text-xs font-medium rounded"
                                  style={{ backgroundColor: `${primaryColor}1a`, color: brandTextColor(primaryColor, 10) }}
                                >
                                  {(program as any).is_tryout_event
                                    ? 'Tryouts'
                                    : program.program_type === 'team' && program.team_type
                                      ? program.team_type === 'competitive_teams'
                                        ? 'All-Star Team'
                                        : 'Rec Team'
                                      : getProgramTypeLabel(program.program_type)
                                  }
                                </span>
                              )}
                              {program.program_type === 'team' && program.season_name && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 bg-gray-50 rounded">
                                  <GoogleMaterialIcon name="calendar_today" size="sm" />
                                  {program.season_name}
                                </span>
                              )}
                              {program.program_type === 'class' && !program.is_open_gym && program.class_category && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-700">
                                  {program.class_category}
                                </span>
                              )}
                              {program.program_type === 'class' && !program.is_open_gym && !program.is_session_based && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-green-50 text-green-700">
                                  Ongoing
                                </span>
                              )}
                              {isDropInOffered(program) && (
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded"
                                  style={{ backgroundColor: `${primaryColor}1a`, color: brandTextColor(primaryColor, 10) }}
                                >
                                  Drop-in Available
                                </span>
                              )}
                              {isDropInOffered(program) && (() => {
                                const memberPrice = memberDropInPriceToShow({
                                  isOpenGym: program.is_open_gym === true,
                                  scheduleCount: Array.isArray(program.sections) ? program.sections.length : null,
                                  memberDropInEnabled: Boolean(program.member_drop_in_enabled),
                                  memberDropInRate: program.member_drop_in_rate,
                                  standardDropInRate: program.drop_in_rate,
                                });
                                if (memberPrice == null) return null;
                                return (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-green-50 text-green-700">
                                    ${program.drop_in_rate} / ${memberPrice} members
                                  </span>
                                );
                              })()}
                            </div>
                          </div>
                        </div>

                        {program.description && (
                          <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                            {program.description}
                          </p>
                        )}

                        {/* Program Details */}
                        <div className="space-y-2 mb-4">
                          {/* Open Gym "Next session" line — topmost, the single most
                              decision-relevant fact ("can I go today?") for a drop-in
                              shopper. Open gyms only; omitted (silent) when there's no
                              upcoming session, matching every non-open-gym card. */}
                          {program.program_type === 'class' && program.is_open_gym && program.next_session && (
                            <div className="flex items-center gap-2 text-sm">
                              <GoogleMaterialIcon name="schedule" size="sm" style={{ color: brandTextColor(primaryColor) }} />
                              <span className="text-gray-700">
                                Next session:{' '}
                                <span className="font-semibold text-gray-900">
                                  {program.next_session.dayLabel} · {formatTimeTo12Hour(program.next_session.startTime)}
                                </span>
                              </span>
                            </div>
                          )}

                          {program.age_range_min && program.age_range_max && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <GoogleMaterialIcon name="cake" size="sm" />
                              <span>Ages {program.age_range_min}-{program.age_range_max}</span>
                            </div>
                          )}

                          {/* Multi-day tryout display - Grouped by Date */}
                          {program.event_days && program.event_days.length > 0 ? (
                            (() => {
                              // Group time slots by date
                              const slotsByDate = new Map<string, typeof program.event_days>();
                              for (const slot of program.event_days!) {
                                const dateKey = slot.event_date;
                                if (!slotsByDate.has(dateKey)) {
                                  slotsByDate.set(dateKey, []);
                                }
                                slotsByDate.get(dateKey)!.push(slot);
                              }
                              const sortedDates = Array.from(slotsByDate.keys()).sort();
                              const uniqueDayCount = sortedDates.length;
                          
                              return (
                                <div className="text-sm text-gray-600">
                                  <div className="flex items-center gap-2 mb-2">
                                    <GoogleMaterialIcon name="event" size="sm" />
                                    <span className="font-medium">
                                      EVALUATION {uniqueDayCount === 1 ? 'DATE' : 'DATES'}
                                    </span>
                                  </div>
                                  <div className="ml-6 space-y-2">
                                    {sortedDates.map((dateKey) => {
                                      const slots = slotsByDate.get(dateKey)!;
                                      slots.sort((a, b) => a.start_time.localeCompare(b.start_time));
                                  
                                      return (
                                        <div key={dateKey}>
                                          <div className="font-medium text-gray-700 text-xs mb-0.5">
                                            {parseDateLocal(dateKey).toLocaleDateString('en-US', {
                                              weekday: 'short',
                                              month: 'short',
                                              day: 'numeric',
                                              year: 'numeric'
                                            })}
                                          </div>
                                          <div className="space-y-0.5 ml-2">
                                            {slots.map((slot, idx) => (
                                              <div key={idx} className="text-gray-600 text-xs flex items-center gap-1">
                                                <span className="text-gray-400">•</span>
                                                <span>{formatTimeTo12Hour(slot.start_time)} - {formatTimeTo12Hour(slot.end_time)}</span>
                                                {slot.day_label && (
                                                  <span className="italic text-gray-500">({slot.day_label})</span>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })()
                          ) : (
                            <div className="space-y-2">
                              {(() => {
                                const todayStr = getGymNowParts(gymTimeZone).dateStr;
                                const weekSections = (program.weekly_day_groups || [])
                                  .map((group) => ({
                                    group,
                                    summary: summarizeWeeklyDays(group.schedules, group, todayStr),
                                  }))
                                  .filter(({ summary }) => summary.days.length > 0)
                                  .sort((a, b) => a.summary.days[0] - b.summary.days[0] || a.group.schedule_number - b.group.schedule_number);
                                if (weekSections.length === 0) {
                                  return program.schedule_description ? (
                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                      <GoogleMaterialIcon name="schedule" size="sm" />
                                      <span>{program.schedule_description}</span>
                                    </div>
                                  ) : null;
                                }
                                return (
                                  <div className="space-y-3">
                                    {weekSections.map(({ group, summary }) => (
                                      <div key={group.id} className="space-y-1.5">
                                        {weekSections.length > 1 && (
                                          <div className="text-xs font-semibold text-gray-700">
                                            {group.section_name || `Schedule ${group.schedule_number}`}
                                          </div>
                                        )}
                                        <DayBadges
                                          highlightedDays={summary.days.map(dayCodeFromIndex).filter(Boolean) as DayCode[]}
                                          size="xs"
                                          labelStyle="letter"
                                          color={primaryColor}
                                          className="gap-1"
                                        />
                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                          <GoogleMaterialIcon name="schedule" size="sm" />
                                          <span>
                                            {summary.time
                                              ? `${formatTimeTo12Hour(summary.time.start_time)} - ${formatTimeTo12Hour(summary.time.end_time)}`
                                              : 'Various times'}
                                          </span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                );
                              })()}
                          
                              {(program.sections && program.sections.length > 0) && (
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSelectedProgramForSchedule(program);
                                  }}
                                  className={`text-xs font-medium hover:opacity-80 flex items-center gap-1 transition-colors`}
                                  style={{ color: brandTextColor(primaryColor) }}
                                >
                                  <GoogleMaterialIcon name="calendar_view_day" size="xs" />
                                  View Detailed Schedule
                                </button>
                              )}

                              {/* Fixed-term program date range */}
                              {program.is_session_based && program.start_date && program.end_date && (
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                  <GoogleMaterialIcon name="date_range" size="sm" />
                                  <span>
                                    {program.start_date === program.end_date
                                      ? formatDate(program.start_date)
                                      : `${formatDate(program.start_date)} – ${formatDate(program.end_date)}`}
                                  </span>
                                </div>
                              )}
                              {/* #4724: an ongoing class listed before its first class.
                                  Raw date string into formatDate (date-only footgun). */}
                              {program.program_type === 'class' && !program.is_session_based && program.starts_on && (
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                  <GoogleMaterialIcon name="event" size="sm" />
                                  <span>Starts {formatDate(program.starts_on)}</span>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Registration Opens Date (for evaluations) */}
                          {program.registration_opens_at && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <GoogleMaterialIcon name="event_available" size="sm" />
                              <span>
                                Registration Opens: {formatDate(program.registration_opens_at)}
                              </span>
                            </div>
                          )}

                          {/* Registration Closes Date (for evaluations) */}
                          {program.registration_closes_at && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <GoogleMaterialIcon name="event_busy" size="sm" />
                              <span>
                                Registration Closes: {formatDate(program.registration_closes_at)}
                              </span>
                            </div>
                          )}

                          {/* Location now displayed prominently above program name */}
                        </div>

                        {/* Pricing Display */}
                        {(program.registration_type === 'direct_enrollment' || (program as any).is_tryout_event) && (
                          <div className="border-t border-gray-200 pt-4 mb-4">
                            {/* Evaluation Fee */}
                            {(program as any).is_tryout_event && (
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">Evaluation Fee</span>
                                <span className="text-lg font-bold text-gray-900 text-right">
                                  {program.member_trial_rate !== null && program.member_trial_rate !== undefined ? (
                                    <>
                                      ${program.member_trial_rate.toFixed(2)} current athletes
                                      <br />
                                      <span className="text-sm font-medium text-gray-600">
                                        ${(program.trial_rate || 0).toFixed(2)} new athletes
                                      </span>
                                    </>
                                  ) : (
                                    program.trial_rate && program.trial_rate > 0 ? `$${program.trial_rate}` : 'Free'
                                  )}
                                </span>
                              </div>
                            )}

                            {/* Class Pricing - Fixed-Term vs Monthly. OPEN GYM (U5): drop-in only —
                                the "$X / $Y members" drop-in badge above is the price; suppress the
                                monthly/session footer so the card never shows a spurious "Monthly /mo". */}
                            {!(program.program_type === 'class' && program.is_open_gym) && (
                              <>
                                {/* Fixed-term program: show total price + payment plan breakdown */}
                                {program.is_session_based && program.session_total_price != null ? (
                                  <div>
                                    <div className="flex items-center justify-between">
                                      <span className="text-sm text-gray-600">Program Price</span>
                                      <span className="text-lg font-bold text-gray-900">
                                        ${program.session_total_price}
                                      </span>
                                    </div>
                                    {(() => {
                                      const count = installmentCountFromPlan(program.session_payment_plan);
                                      if (count === null || count < 2) return null;
                                      return (
                                        <p className="text-xs text-gray-500 mt-1">
                                          {SESSION_PAYMENT_PLAN_OPTIONS[program.session_payment_plan as SessionPaymentPlan]}
                                        </p>
                                      );
                                    })()}
                                  </div>
                                ) : (
                                  /* Ongoing class: show monthly rate */
                                  classSellsMonthly(program) && program.monthly_rate != null && (
                                    <div className="flex items-center justify-between">
                                      <span className="text-sm text-gray-600">Monthly</span>
                                      <span className="text-lg font-bold text-gray-900">
                                        ${program.monthly_rate}/mo
                                      </span>
                                    </div>
                                  )
                                )}
                              </>
                            )}
                          </div>
                        )}

                        {/* Enrollment Button */}
                        <div className="space-y-2">
                          {/* Every card has the same View Details button: it opens the
                              program's own page (browsing) and starts nothing. Why this
                              viewer cannot sign up right now — closed, ended, locked,
                              staff login, athlete login, roster limit, family access —
                              is the grey label under it (cardStatus below). The
                              program's own page holds back its sign-up button for the
                              same reasons and says why in full. */}
                          <Link
                            href={programHref}
                            className="w-full px-4 py-2.5 text-white rounded-lg text-sm font-medium flex items-center justify-center transition-colors hover:brightness-95"
                            style={{ backgroundColor: primaryColor, color: readableTextOn(primaryColor) }}
                          >
                            View Details
                          </Link>

                          {/* Availability Badge */}
                          <div className="flex items-center justify-center">
                            {getAvailabilityBadge(program, cardStatus)}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>

      {/* Schedule Modal */}
      <Modal
        isOpen={!!selectedProgramForSchedule}
        onClose={() => setSelectedProgramForSchedule(null)}
        className="max-w-2xl"
      >
        <Modal.Header>
          <div className="flex items-center gap-3">
            <div 
              className="p-2 rounded-lg"
              style={{ backgroundColor: `${primaryColor}1a` }}
            >
              <GoogleMaterialIcon 
                name="calendar_month" 
                style={{ color: brandTextColor(primaryColor, 10) }} 
                size="md" 
              />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">{selectedProgramForSchedule?.name}</h3>
              <p className="text-sm text-gray-500">Program Schedule & Availability</p>
            </div>
          </div>
        </Modal.Header>

        <Modal.Content scrollable>
          <div className="space-y-6">
            {/* Schedule List */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2">
                Available Schedule
                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-[10px] font-normal">
                  {selectedProgramForSchedule?.sections?.length || 0} {(selectedProgramForSchedule?.sections?.length || 0) === 1 ? 'SCHEDULE' : 'SCHEDULES'}
                </span>
              </h4>

              <div className="grid gap-4">
                {selectedProgramForSchedule?.sections?.map((section) => {
                  const dayMap: Record<number, string> = {
                    0: 'Sunday', 1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 
                    4: 'Thursday', 5: 'Friday', 6: 'Saturday'
                  };
                  
                  // Get schedules array (new grouped format) or single schedule (legacy format)
                  const schedules = section.schedules || [];
                  const hasMultipleSchedules = schedules.length > 1;
                  
                  return (
                    <div 
                      key={section.id} 
                      className="rounded-xl border border-gray-200 bg-white overflow-hidden"
                    >
                      {/* Schedule Header */}
                      <div className="p-4 border-b border-gray-100">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h5 className="font-bold text-gray-900 mb-2">
                              {section.section_name || 'Schedule'}
                            </h5>
                            
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
                              {renderScheduleSpotCount(section)}
                              {section.capacity > 0 && (
                                <div className="flex items-center gap-1.5">
                                  <GoogleMaterialIcon name="people" size="xs" className="text-gray-400" />
                                  <span>
                                    Capacity: {section.capacity}{' '}
                                    {selectedProgramForSchedule?.program_type === 'class' &&
                                    selectedProgramForSchedule?.is_open_gym
                                      ? 'per session'
                                      : 'students'}
                                  </span>
                                </div>
                              )}
                              {section.location && (
                                <div className="flex items-center gap-1.5">
                                  <GoogleMaterialIcon name="location_on" size="xs" className="text-gray-400" />
                                  {section.location.name}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Rate Info — the "Rates" label is rendered only when at least one
                              rate is actually shown, otherwise a class with drop-ins off and no
                              monthly rate (e.g. a fixed-term session) leaves a bare heading over
                              empty space (#3358). */}
                          {((selectedProgramForSchedule?.drop_in_enabled !== false && section.drop_in_rate != null)
                            || (!selectedProgramForSchedule?.is_session_based
                              && (!selectedProgramForSchedule || classSellsMonthly(selectedProgramForSchedule))
                              && section.monthly_rate != null)) ? (
                          <div className="flex-shrink-0 text-right">
                            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none mb-1">Rates</div>
                            <div className="flex items-baseline gap-2">
                              {/* #3358: gate on the toggle as well as the price. The schedule
                                  carries the class drop-in rate, so a class with "Allow Drop-Ins"
                                  OFF was still advertising a rate on this card while the program
                                  -level badge above correctly hid it. `!== false` keeps a missing
                                  flag meaning ENABLED, matching the column default. */}
                              {/* #4968: != null so a free ($0) drop-in shows as $0, not a stray "0". */}
                              {selectedProgramForSchedule?.drop_in_enabled !== false && section.drop_in_rate != null && (
                                <div className="flex flex-col items-end">
                                  <span
                                    className="font-bold text-sm"
                                    style={{ color: brandTextColor(primaryColor) }}
                                  >
                                    ${section.drop_in_rate}
                                  </span>
                                  <span 
                                    className="text-[9px] font-medium uppercase"
                                    style={{ color: brandTextColor(primaryColor) }}
                                  >
                                    Drop-in
                                  </span>
                                </div>
                              )}
                              {/* Section pricing: hide monthly rate for fixed-term programs */}
                              {!selectedProgramForSchedule?.is_session_based
                                && (!selectedProgramForSchedule || classSellsMonthly(selectedProgramForSchedule))
                                && section.monthly_rate != null && (
                                <div className="flex flex-col items-end pl-2 border-l border-gray-200">
                                  <span className="text-gray-900 font-bold text-sm">${section.monthly_rate}/mo</span>
                                  <span className="text-[9px] text-gray-500 font-medium uppercase">Membership</span>
                                </div>
                              )}
                            </div>
                          </div>
                          ) : null}
                        </div>
                      </div>

                      {/* Schedules List */}
                      <div className="p-4 pt-3">
                        {hasMultipleSchedules ? (
                          <div className="space-y-2">
                            {schedules.map((schedule: any, index: number) => (
                              <div 
                                key={schedule.id || index}
                                className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50"
                              >
                                {/* Day Badge */}
                                <div 
                                  className="flex-shrink-0 w-10 h-10 rounded-lg flex flex-col items-center justify-center text-white shadow-sm"
                                  style={{ backgroundColor: primaryColor, color: readableTextOn(primaryColor) }}
                                >
                                  <span className="text-[10px] font-bold uppercase leading-none opacity-90">
                                    {dayMap[schedule.day_of_week]?.substring(0, 3) || '?'}
                                  </span>
                                  <GoogleMaterialIcon name="schedule" size="xs" className="mt-0.5 opacity-70" />
                                </div>

                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-semibold text-gray-900 text-sm">
                                      {dayMap[schedule.day_of_week] || 'Unknown'}
                                    </span>
                                    <span className="text-gray-400">•</span>
                                    <span 
                                      className="font-semibold text-sm"
                                      style={{ color: brandTextColor(primaryColor, 0, SURFACE_GRAY_50) }}
                                    >
                                      {formatTimeTo12Hour(schedule.start_time)} - {formatTimeTo12Hour(schedule.end_time)}
                                    </span>
                                  </div>
                                  
                                  {/* Schedule-specific location and coaches */}
                                  {(schedule.location?.id !== section.location?.id || renderScheduleCoaches(schedule)) && (
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                                      {schedule.location && schedule.location.id !== section.location?.id && (
                                        <div className="flex items-center gap-1">
                                          <GoogleMaterialIcon name="location_on" size="xs" className="text-gray-400" />
                                          {schedule.location.name}
                                        </div>
                                      )}
                                      {renderScheduleCoaches(schedule)}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          // Single schedule - show inline (backward compatibility with legacy format)
                          schedules.length === 1 ? (
                            <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50">
                              <div 
                                className="flex-shrink-0 w-10 h-10 rounded-lg flex flex-col items-center justify-center text-white shadow-sm"
                                style={{ backgroundColor: primaryColor, color: readableTextOn(primaryColor) }}
                              >
                                <span className="text-[10px] font-bold uppercase leading-none opacity-90">
                                  {dayMap[schedules[0].day_of_week]?.substring(0, 3) || '?'}
                                </span>
                                <GoogleMaterialIcon name="schedule" size="xs" className="mt-0.5 opacity-70" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-gray-900 text-sm">
                                    {dayMap[schedules[0].day_of_week] || 'Unknown'}
                                  </span>
                                  <span className="text-gray-400">•</span>
                                  <span 
                                    className="font-semibold text-sm"
                                    style={{ color: brandTextColor(primaryColor, 0, SURFACE_GRAY_50) }}
                                  >
                                    {formatTimeTo12Hour(schedules[0].start_time)} - {formatTimeTo12Hour(schedules[0].end_time)}
                                  </span>
                                </div>
                                <div className="mt-1">
                                  {renderScheduleCoaches(schedules[0])}
                                </div>
                              </div>
                            </div>
                          ) : (
                            // Legacy format: single schedule entry (no schedules array)
                            section.day_of_week !== undefined ? (
                              <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50">
                                <div 
                                  className="flex-shrink-0 w-10 h-10 rounded-lg flex flex-col items-center justify-center text-white shadow-sm"
                                  style={{ backgroundColor: primaryColor, color: readableTextOn(primaryColor) }}
                                >
                                  <span className="text-[10px] font-bold uppercase leading-none opacity-90">
                                    {dayMap[section.day_of_week]?.substring(0, 3) || '?'}
                                  </span>
                                  <GoogleMaterialIcon name="schedule" size="xs" className="mt-0.5 opacity-70" />
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-gray-900 text-sm">
                                      {dayMap[section.day_of_week] || 'Unknown'}
                                    </span>
                                    <span className="text-gray-400">•</span>
                                  <span 
                                    className="font-semibold text-sm"
                                    style={{ color: brandTextColor(primaryColor, 0, SURFACE_GRAY_50) }}
                                  >
                                    {formatTimeTo12Hour(section.start_time)} - {formatTimeTo12Hour(section.end_time)}
                                  </span>
                                </div>
                                <div className="mt-1">
                                  {renderScheduleCoaches(section)}
                                </div>
                              </div>
                            </div>
                            ) : null
                          )
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-amber-50 rounded-xl p-4 border border-amber-100 flex items-start gap-3">
              <GoogleMaterialIcon name="info" className="text-amber-600 mt-0.5" size="sm" />
              <div>
                <h5 className="text-sm font-bold text-amber-900 mb-1">Booking Options</h5>
                <p className="text-xs text-amber-800 leading-relaxed">
                  You can choose to enroll in a <strong className="font-bold">Monthly Membership</strong> for continuous training and guaranteed spots, or book individual <strong className="font-bold">Drop-in Sessions</strong> for ultimate flexibility. Click "View Details" on the program card to pick your option.
                </p>
              </div>
            </div>
          </div>
        </Modal.Content>

        <Modal.Footer variant="actions" className="js-schedule-modal-footer">
          {/* FAMILY / GUARDIAN ACCESS — "Enroll or Book Now" starts a register/book
              action, so it is gated the same way as the card CTA. When the viewer
              is acting as a family they cannot register for, show the reason
              instead of the button. Inert for flag-off / guests / primary guardians
              / cross-org mismatch (see blockFamilyRegistration). */}
          {/* The card's grey label is not enough on its own: this modal opens from
              the card's "View Detailed Schedule" and carries its OWN register action,
              so a locked offering was reachable in two clicks from the very card
              showing the label. Same non-interactive treatment, checked first. */}
          {selectedProgramForSchedule?.registration_locked ? (
            <div
              className="w-full px-4 py-2.5 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium flex items-center justify-center"
              data-testid="registration-locked-modal-pill"
            >
              <GoogleMaterialIcon name="block" size="sm" className="mr-1.5" />
              Not available right now
            </div>
          ) : blockFamilyRegistration ? (
            familyBlockedCta
          ) : (
            <Button
              type="button"
              size="md"
              variant="none"
              onClick={() => {
                const program = selectedProgramForSchedule!;
                const programHref = program.program_type === 'private_lessons'
                  ? `/programs/${gymSlug}/private-lessons/${program.id}/book`
                  : `/programs/${gymSlug}/${program.program_type}/${program.id}`;
                // New tab on web, navigate in place inside the app's webview
                // sheet — where Android drops a `target=_blank` open into a
                // WebView it never displays. See openProgramTarget.
                openProgramTarget({
                  href: programHref,
                  isMobileWebView,
                  push: pushHref,
                  openWindow: openHrefInNewTab,
                });
                setSelectedProgramForSchedule(null);
              }}
              className="text-white shadow-sm hover:brightness-95"
              style={{ backgroundColor: primaryColor, color: readableTextOn(primaryColor) }}
            >
              Enroll or Book Now
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
}
